import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, Check, X, ChevronRight, Star, Award, Lightbulb } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

export default function LessonDetail() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('learn'); // learn | quiz | results
  const [quizItems, setQuizItems] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [completing, setCompleting] = useState(false);

  useEffect(() => { loadLesson(); }, [lessonId]); // eslint-disable-line

  const loadLesson = async () => {
    try {
      const res = await api.get(`/lessons/${lessonId}`);
      setData(res.data);
    } catch { setData(null); }
    setLoading(false);
  };

  const startQuiz = async () => {
    try {
      const res = await api.get(`/lessons/${lessonId}/quiz`);
      setQuizItems(res.data.quiz_items || []);
      setCurrentQ(0);
      setAnswers({});
      setResults(null);
      setMode('quiz');
    } catch (err) { console.error(err) }
  };

  const selectAnswer = (quizItemId, answer) => {
    setAnswers(prev => ({ ...prev, [quizItemId]: answer }));
  };

  const submitQuiz = async () => {
    setSubmitting(true);
    try {
      const answerList = quizItems.map(q => ({
        quiz_item_id: q.quiz_item_id,
        user_answer: answers[q.quiz_item_id] || ''
      }));
      const res = await api.post(`/lessons/${lessonId}/submit-quiz`, {
        lesson_id: lessonId,
        answers: answerList
      });
      setResults(res.data);
      setMode('results');
    } catch (err) { console.error(err) }
    setSubmitting(false);
  };

  const completeLesson = async () => {
    setCompleting(true);
    try {
      await api.post(`/lessons/${lessonId}/complete`);
      await loadLesson();
    } catch (err) { console.error(err) }
    setCompleting(false);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-[#E7E5E4] border-t-[#4CAF50] rounded-full animate-spin" />
    </div>
  );

  if (!data?.lesson) return (
    <div className="max-w-3xl mx-auto px-4 py-12 text-center">
      <p className="text-[#57534E]">Lesson not found</p>
      <Link to="/lessons" className="text-[#4CAF50] mt-2 inline-block">Back to Lessons</Link>
    </div>
  );

  const { lesson, items, rules, progress } = data;

  // --- RESULTS VIEW ---
  if (mode === 'results' && results) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12 pb-safe">
        <div className="text-center mb-8">
          <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${
            results.completed ? 'bg-[#EAF1EA]' : 'bg-[#E8F5E9]'
          }`}>
            {results.completed ? <Award className="w-10 h-10 text-[#588157]" /> : <BookOpen className="w-10 h-10 text-[#4CAF50]" />}
          </div>
          <h2 className="text-2xl font-bold text-[#1C1917]" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="quiz-results-title">
            {results.completed ? 'Lesson Complete!' : 'Keep Practicing'}
          </h2>
          <p className="text-[#57534E] mt-1">
            {results.correct} of {results.total} correct ({results.score}%)
          </p>
          {results.xp_earned > 0 && (
            <p className="text-[#D4A373] font-semibold mt-2 flex items-center justify-center gap-1">
              <Star className="w-4 h-4" /> +{results.xp_earned} XP earned
            </p>
          )}
        </div>

        <div className="space-y-3 mb-8" data-testid="quiz-results-list">
          {results.results.map((r, i) => (
            <div key={r.quiz_item_id} className={`p-4 rounded-xl border ${r.is_correct ? 'border-[#588157]/30 bg-[#EAF1EA]/50' : 'border-[#4CAF50]/30 bg-[#E8F5E9]/50'}`}>
              <div className="flex items-start gap-2">
                {r.is_correct ? <Check className="w-4 h-4 text-[#588157] mt-0.5" /> : <X className="w-4 h-4 text-[#4CAF50] mt-0.5" />}
                <div>
                  <p className="text-sm text-[#1C1917]">{quizItems[i]?.prompt_text}</p>
                  {!r.is_correct && (
                    <p className="text-xs text-[#4CAF50] mt-1">Correct: {r.correct_answer}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-3">
          <Button onClick={() => { setMode('learn'); }} variant="outline" className="flex-1 rounded-xl" data-testid="review-lesson-btn">
            Review Lesson
          </Button>
          {!results.completed && (
            <Button onClick={startQuiz} className="flex-1 bg-[#4CAF50] hover:bg-[#3D8B40] text-white rounded-xl" data-testid="retry-quiz-btn">
              Try Again
            </Button>
          )}
          <Link to="/lessons" className="flex-1">
            <Button variant="outline" className="w-full rounded-xl" data-testid="back-to-lessons-btn">
              All Lessons
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // --- QUIZ VIEW ---
  if (mode === 'quiz' && quizItems.length > 0) {
    const q = quizItems[currentQ];
    const answered = answers[q?.quiz_item_id];
    const isLast = currentQ === quizItems.length - 1;
    const allAnswered = quizItems.every(qi => answers[qi.quiz_item_id]);

    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10 pb-safe">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => setMode('learn')} className="text-sm text-[#57534E] hover:text-[#1C1917]" data-testid="exit-quiz">
            <ArrowLeft className="w-4 h-4 inline mr-1" /> Exit Quiz
          </button>
          <span className="text-xs font-semibold text-[#A8A29E]">{currentQ + 1} / {quizItems.length}</span>
        </div>
        <Progress value={((currentQ + 1) / quizItems.length) * 100} className="h-2 bg-[#F5F5F4] mb-8" />

        <div className="bg-white rounded-2xl border border-[#E7E5E4] p-6 sm:p-8 mb-6" data-testid={`quiz-question-${currentQ}`}>
          {q.prompt_lamnso && (
            <p className="text-xl font-semibold text-[#4CAF50] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{q.prompt_lamnso}</p>
          )}
          <p className="text-lg text-[#1C1917] mb-6">{q.prompt_text}</p>

          {q.options && (
            <div className="space-y-3">
              {q.options.map((opt, i) => (
                <button key={`idx-${i}`}
                  onClick={() => selectAnswer(q.quiz_item_id, opt)}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                    answered === opt
                      ? 'border-[#4CAF50] bg-[#E8F5E9]'
                      : 'border-[#E7E5E4] hover:border-[#D4A373] hover:bg-[#FDF6F0]'
                  }`}
                  data-testid={`quiz-option-${i}`}
                >
                  <span className="text-sm font-medium text-[#1C1917]">{opt}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-3">
          {currentQ > 0 && (
            <Button onClick={() => setCurrentQ(currentQ - 1)} variant="outline" className="rounded-xl" data-testid="prev-question">
              Previous
            </Button>
          )}
          <div className="flex-1" />
          {!isLast ? (
            <Button onClick={() => setCurrentQ(currentQ + 1)} disabled={!answered}
              className="bg-[#4CAF50] hover:bg-[#3D8B40] text-white rounded-xl" data-testid="next-question">
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={submitQuiz} disabled={!allAnswered || submitting}
              className="bg-[#588157] hover:bg-[#436442] text-white rounded-xl" data-testid="submit-quiz-btn">
              {submitting ? 'Submitting...' : 'Submit Quiz'}
            </Button>
          )}
        </div>
      </div>
    );
  }

  // --- LEARN VIEW ---
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10 pb-safe">
      <Link to="/lessons" className="inline-flex items-center gap-1 text-sm text-[#57534E] hover:text-[#1C1917] mb-6" data-testid="back-to-lessons">
        <ArrowLeft className="w-4 h-4" /> Back to Lessons
      </Link>

      {/* Lesson Header */}
      <div className="bg-white rounded-2xl border border-[#E7E5E4] p-6 sm:p-8 mb-6" data-testid="lesson-header">
        <div className="flex items-start justify-between">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#A8A29E]">{lesson.level}</span>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1C1917] mt-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {lesson.title_en}
            </h1>
            {lesson.title_lamnso && (
              <p className="text-sm text-[#D4A373] font-medium mt-1">{lesson.title_lamnso}</p>
            )}
          </div>
          {progress?.completed && (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#EAF1EA] text-[#588157] text-xs font-bold" data-testid="completed-badge">
              <Check className="w-3 h-3" /> Completed
            </span>
          )}
        </div>
        {lesson.learning_objectives?.length > 0 && (
          <div className="mt-4 p-3 bg-[#F5F5F4] rounded-xl">
            <p className="text-xs font-bold text-[#57534E] mb-1">You will learn:</p>
            <ul className="space-y-1">
              {lesson.learning_objectives.map((obj, i) => (
                <li key={`idx-${i}`} className="text-xs text-[#57534E] flex gap-2">
                  <span className="text-[#4CAF50] mt-0.5">-</span> {obj}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Literacy Rules */}
      {rules?.length > 0 && (
        <div className="bg-[#FDF6F0] rounded-2xl border border-[#D4A373]/30 p-5 sm:p-6 mb-6" data-testid="lesson-rules">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb className="w-4 h-4 text-[#D4A373]" />
            <h3 className="text-sm font-semibold text-[#1C1917]">Key Rules</h3>
          </div>
          <div className="space-y-3">
            {rules.map(rule => (
              <div key={rule.rule_id} className="text-sm" data-testid={`rule-${rule.rule_id}`}>
                <p className="font-semibold text-[#1C1917]">{rule.title_en}</p>
                <p className="text-[#57534E] text-xs mt-0.5">{rule.rule_text}</p>
                {rule.short_hint && (
                  <p className="text-xs text-[#D4A373] font-medium mt-0.5">{rule.short_hint}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lesson Items */}
      {items?.length > 0 && (
        <div className="space-y-4 mb-8" data-testid="lesson-items">
          {items.map((item) => (
            <div key={item.lesson_item_id}
              className={`bg-white rounded-2xl border border-[#E7E5E4] p-5 sm:p-6 ${
                item.item_type === 'teaching_card' ? '' : 'border-l-4 border-l-[#D4A373]'
              }`}
              data-testid={`item-${item.lesson_item_id}`}
            >
              {item.item_type === 'teaching_card' && (
                <span className="text-[10px] font-bold text-[#A8A29E] uppercase tracking-wider">{item.prompt_type?.replace(/_/g, ' ')}</span>
              )}
              <div className="mt-2">
                {item.lamnso && (
                  <p className="text-xl font-bold text-[#4CAF50]" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.lamnso}</p>
                )}
                <p className="text-sm text-[#1C1917] mt-1 leading-relaxed">{item.display_text}</p>
                {item.explanation && (
                  <p className="text-xs text-[#57534E] mt-2 p-2.5 bg-[#F5F5F4] rounded-lg">{item.explanation}</p>
                )}
                {item.example_lamnso && (
                  <div className="mt-3 flex items-center gap-2 text-xs">
                    <span className="font-semibold text-[#4CAF50]">{item.example_lamnso}</span>
                    <span className="text-[#A8A29E]">=</span>
                    <span className="text-[#57534E]">{item.example_english}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3" data-testid="lesson-actions">
        {isAuthenticated && (
          <>
            <Button onClick={startQuiz}
              className="flex-1 bg-[#4CAF50] hover:bg-[#3D8B40] text-white rounded-xl gap-1.5"
              data-testid="start-quiz-btn"
            >
              <BookOpen className="w-4 h-4" /> Take Quiz
            </Button>
            {!progress?.completed && (
              <Button onClick={completeLesson} disabled={completing} variant="outline"
                className="rounded-xl gap-1.5 border-[#588157] text-[#588157]"
                data-testid="mark-complete-btn"
              >
                <Check className="w-4 h-4" /> {completing ? '...' : 'Mark Complete'}
              </Button>
            )}
          </>
        )}
        {!isAuthenticated && (
          <Link to="/login" className="flex-1">
            <Button className="w-full bg-[#4CAF50] hover:bg-[#3D8B40] text-white rounded-xl">
              Sign in to track progress
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
}
